// /connect/:requestId: where Omni's OAuth server sends Claude, ChatGPT or a dealer's
// own system to ask to act as someone (plan 3.5, mock M8). The person signs in with
// Google, Microsoft or their email link if they aren't (mock M2 states 33 and 34), sees
// who is asking and what it could do, and allows or denies it with their ID token.
// Either way Omni sends them back to the app.
import React, { useEffect, useState } from "react";
import { isDealerRole, type Me } from "@phoneware/lens-domain";
import { ApiError, type LensApi } from "../api.js";
import {
  CANT_DO,
  WRITE_SCOPE,
  disabledUser,
  hostOf,
  readScope,
  requestExpiresAt,
  roleAt,
  type OAuthRequestView,
  type UserDisabledRefusal,
} from "../api-oauth.js";
import type { AuthClient, AuthLoader } from "../auth.js";
import { Icon } from "../icons.js";
import { Link } from "../router.js";
import { clock, errorText, firstName } from "../format.js";
import { useTitle } from "../ui.js";
import { AppReq } from "./connect-bits.js";
import { SignInCard, SignInFlow, StateIcon } from "./SignIn.js";
import { methodOf, methodWords } from "./signin-bits.js";

type RequestState = { kind: "loading" } | { kind: "missing" } | { kind: "ok"; r: OAuthRequestView } | { kind: "broken"; message: string };

type Who =
  | { kind: "nobody" }
  | { kind: "loading" }
  | { kind: "me"; me: Me }
  | { kind: "disabled"; user: UserDisabledRefusal["user"] }
  | { kind: "refused"; message: string }
  | { kind: "broken"; message: string };

export interface ConnectPageProps {
  api: LensApi;
  loadAuth: AuthLoader;
  requestId: string;
  /** Leaves Omni for the app's own address. The browser's location by default; tests watch it. */
  leave: (url: string) => void;
}

export function ConnectPage({ api, loadAuth, requestId, leave }: ConnectPageProps): React.ReactElement {
  useTitle("Connect to Omni");
  const [auth, setAuth] = useState<AuthClient | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [authProblem, setAuthProblem] = useState<string | null>(null);
  const [req, setReq] = useState<RequestState>({ kind: "loading" });
  const [who, setWho] = useState<Who>({ kind: "loading" });
  const [gone, setGone] = useState(false);
  const [done, setDone] = useState<{ allow: boolean; redirectTo: string } | null>(null);
  const [busy, setBusy] = useState<"allow" | "deny" | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    loadAuth().then(
      (client) => {
        if (!live) return;
        setAuth(client);
        setEmail(client.email());
      },
      (err: unknown) => {
        if (live) setAuthProblem(errorText(err));
      },
    );
    return () => {
      live = false;
    };
  }, [loadAuth]);

  useEffect(() => (auth ? auth.subscribe(() => setEmail(auth.email())) : undefined), [auth]);

  useEffect(() => {
    let live = true;
    setReq({ kind: "loading" });
    api.oauthRequest(requestId).then(
      (r) => {
        if (live) setReq({ kind: "ok", r });
      },
      (err: unknown) => {
        if (!live) return;
        setReq(err instanceof ApiError && err.status === 404 ? { kind: "missing" } : { kind: "broken", message: errorText(err) });
      },
    );
    return () => {
      live = false;
    };
  }, [api, requestId]);

  // Who would allow it: the invited person signed in here. A turned-off person's sign-in still works with Google, so
  // Omni refuses /api/me; the request read with their token says whether that is "turned off" and names them.
  useEffect(() => {
    if (!auth) return;
    if (!email) {
      setWho({ kind: "nobody" });
      return;
    }
    let live = true;
    setWho({ kind: "loading" });
    void (async () => {
      try {
        const me = await api.me();
        if (live) setWho({ kind: "me", me });
      } catch (err) {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) {
          void auth.signOut();
          return;
        }
        if (!(err instanceof ApiError && err.status === 403)) {
          setWho({ kind: "broken", message: errorText(err) });
          return;
        }
        try {
          await api.oauthRequestAsMe(requestId);
          if (live) setWho({ kind: "refused", message: err.message });
        } catch (again) {
          if (!live) return;
          const user = disabledUser(again);
          setWho(user ? { kind: "disabled", user } : { kind: "refused", message: err.message });
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [api, auth, email, requestId]);

  // The page can sit open past the request's ten minutes: redraw as expired when they run out.
  const expiresAt = req.kind === "ok" ? requestExpiresAt(req.r) : null;
  useEffect(() => {
    if (expiresAt === null) return;
    const left = expiresAt - Date.now();
    if (left <= 0) return;
    const timer = window.setTimeout(() => setTick((n) => n + 1), left + 50);
    return () => window.clearTimeout(timer);
  }, [expiresAt]);

  useEffect(() => {
    if (done) leave(done.redirectTo);
  }, [done, leave]);

  async function decide(allow: boolean): Promise<void> {
    setBusy(allow ? "allow" : "deny");
    setProblem(null);
    try {
      const answer = await api.decideOAuthRequest(requestId, { allow });
      setDone({ allow, redirectTo: answer.redirectTo });
    } catch (err) {
      const user = disabledUser(err);
      if (user) setWho({ kind: "disabled", user });
      else if (err instanceof ApiError && err.status === 410) setGone(true);
      else if (err instanceof ApiError && err.status === 404) setReq({ kind: "missing" });
      else if (err instanceof ApiError && err.message === "not_while_acting_as") setProblem("Omni won\u2019t connect an app while you act as someone. Stop first, then connect it as yourself.");
      else setProblem(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  function signOut(): void {
    if (auth) void auth.signOut();
  }

  // How this sign-in was made, as Omni recorded it from the token: "Signed in with Microsoft as ...".
  const lastSignIn = who.kind === "me" ? who.me.user.lastSignIn : null;
  const via = lastSignIn ? methodOf(lastSignIn.provider) : null;
  const signedInAs = (
    <p className="signin-note">
      Signed in{via ? ` with ${methodWords(via)}` : ""} as <b>{email}</b>.{who.kind === "me" ? " Not you? " : " "}
      <button className="link" type="button" data-testid="consent-signout" onClick={signOut}>
        Sign out
      </button>
    </p>
  );

  if (authProblem || req.kind === "broken" || who.kind === "broken") {
    const message = authProblem ?? (req.kind === "broken" ? req.message : who.kind === "broken" ? who.message : "");
    return (
      <SignInCard consent state="broken">
        <h1 data-testid="consent-broken">Omni did not load</h1>
        <p className="signin-lede">{message}</p>
        <button className="btn primary wide" type="button" onClick={() => window.location.reload()}>
          Try again
        </button>
      </SignInCard>
    );
  }

  if (req.kind === "loading") {
    return (
      <SignInCard consent state="loading">
        <h1 data-testid="consent-loading">Opening Omni</h1>
        <p className="signin-lede">One moment.</p>
      </SignInCard>
    );
  }

  const r = req.kind === "ok" ? req.r : null;
  const host = r ? hostOf(r.redirectUri) : "";

  if (done && r) {
    return done.allow ? (
      <SignInCard consent state="allowed">
        <div data-testid="consent-allowed">
          <StateIcon kind="ok" icon="circle-check" />
          <h1>{r.clientName} can now act as you</h1>
          <p className="signin-lede">
            Omni is sending you back to {host} to finish. Ask {r.clientName} about your customers, quotes or orders there.
          </p>
          <p className="signin-note">Revoke it any time in Settings, Connected apps.</p>
        </div>
      </SignInCard>
    ) : (
      <SignInCard consent state="denied">
        <div data-testid="consent-denied">
          <StateIcon icon="ban" />
          <h1>{r.clientName} wasn&rsquo;t connected</h1>
          <p className="signin-lede">
            Omni told {r.clientName} you said no, so it can&rsquo;t act as you. Omni is sending you back to {host}.
          </p>
        </div>
      </SignInCard>
    );
  }

  if (!r || gone || Date.now() >= requestExpiresAt(r)) {
    return (
      <SignInCard consent state="expired">
        <div data-testid="consent-expired">
          <StateIcon kind="warn" icon="clock" />
          <h1>This request has expired</h1>
          <p className="signin-lede">
            {r
              ? `${r.clientName} asked to connect at ${clock(new Date(r.createdAt))}, and a request lasts 10 minutes. Go back to ${r.clientName} and choose Connect again, and Omni will ask you then.`
              : "Omni has no request at this address, and a request lasts 10 minutes. Go back to the app and choose Connect again, and Omni will ask you then."}
          </p>
          <Link className="btn wide" to="/settings/connect">
            <Icon name="arrow-right" size={15} />
            Open Omni
          </Link>
        </div>
      </SignInCard>
    );
  }

  if (who.kind === "nobody") {
    return <SignInFlow api={api} auth={auth} next={`/connect/${encodeURIComponent(requestId)}`} consent={{ client: r.clientName, host, until: clock(new Date(requestExpiresAt(r))) }} />;
  }

  if (who.kind === "loading") {
    return (
      <SignInCard consent state="loading">
        <h1 data-testid="consent-loading">Opening Omni</h1>
        <p className="signin-lede">One moment.</p>
      </SignInCard>
    );
  }

  if (who.kind === "disabled") {
    const u = who.user;
    return (
      <SignInCard consent state="turned-off">
        <div data-testid="consent-turned-off">
          <StateIcon kind="stop" icon="user-x" />
          <h1>Your Omni sign-in is turned off</h1>
          <p className="signin-lede">
            {r.clientName} ({host}) asked to act as {u.name}, {roleAt(u.role, u.dealerName)}. While your sign-in is off, nothing can act as you, so {r.clientName} won&rsquo;t connect.
          </p>
          <p className="signin-note">{u.dealerName && isDealerRole(u.role) ? `The owner at ${u.dealerName} can turn it back on in Settings.` : "A Super admin at Phoneware can turn it back on."}</p>
          {signedInAs}
        </div>
      </SignInCard>
    );
  }

  if (who.kind === "refused") {
    return (
      <SignInCard consent state="refused">
        <div data-testid="consent-refused">
          <StateIcon kind="stop" icon="user-x" />
          <h1>Not invited yet</h1>
          <p className="signin-lede">
            {who.message} {r.clientName} can only act as someone invited to Omni.
          </p>
          {signedInAs}
        </div>
      </SignInCard>
    );
  }

  // While a Super admin acts as someone (mock M2 state 39): a grant made now would outlive the session, so Omni refuses.
  const acting = who.me.actingAs ?? null;
  if (acting) {
    const t = acting.target;
    const first = firstName(t.name);
    return (
      <SignInCard consent state="acting">
        <AppReq client={r.clientName} line={`${host} wants to connect to Omni`} />
        <div data-testid="consent-acting">
          <StateIcon kind="warn" icon="eye-off" />
          <h1>Not while you act as {first}</h1>
          <p className="signin-lede">
            {r.clientName} ({host}) asked to act as {t.name}, {roleAt(t.role, t.dealerName)}. Allowing it would give {r.clientName} a lasting grant, so Omni won&rsquo;t do it while you act
            as someone. {first} can connect {r.clientName}.
          </p>
          <Link className="btn wide" to="/settings/connect">
            <Icon name="arrow-left" size={15} />
            Back to Omni
          </Link>
          <p className="signin-note">
            Signed in as <b>{acting.admin.email}</b>, acting as {t.name}.
          </p>
        </div>
      </SignInCard>
    );
  }

  const { user, dealer } = who.me;
  return (
    <SignInCard consent state="ask">
      <div data-testid="consent-ask">
        <h1>
          {r.clientName} ({host}) wants to act as {user.name}, {roleAt(user.role, dealer?.name ?? null)}
        </h1>
        <p className="signin-lede">It would work in Omni as you and see what you see.</p>
        <ul className="scopes">
          {r.scopes.includes("omni.read") ? (
            <li data-testid="consent-scope-read">
              <Icon name="check" size={16} />
              <span>
                <b>Read</b>
                {readScope(user, dealer)}
              </span>
            </li>
          ) : null}
          {r.scopes.includes("omni.write") ? (
            <li data-testid="consent-scope-write">
              <Icon name="check" size={16} />
              <span>
                <b>Make drafts</b>
                {WRITE_SCOPE}
              </span>
            </li>
          ) : null}
        </ul>
        <div className="cant">
          <Icon name="ban" size={16} />
          <span>
            <b>It can&rsquo;t</b>
            {CANT_DO} You do those in Omni.
          </span>
        </div>
        {problem ? (
          <p className="signin-error" role="alert">
            {problem}
          </p>
        ) : null}
        <button className="btn primary wide" type="button" disabled={busy !== null} data-testid="consent-allow" onClick={() => void decide(true)}>
          {busy === "allow" ? "Allowing" : "Allow"}
        </button>
        <button className="btn wide" type="button" disabled={busy !== null} data-testid="consent-deny" onClick={() => void decide(false)}>
          {busy === "deny" ? "Denying" : "Deny"}
        </button>
        <p className="signin-note">Either way, Omni sends you back to {host}. Revoke it any time in Settings, Connected apps.</p>
        {signedInAs}
      </div>
    </SignInCard>
  );
}
